import { forwardRef, type ReactNode } from "react";
import styled from "@emotion/styled";
import { RevealAndCopy, type RevealAndCopyProps } from "@/components/ui/patterns/RevealAndCopy";

export type RevealFieldProps = Omit<RevealAndCopyProps, "className"> & {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  className?: string;
  fieldClassName?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline.
 *
 * Cascade notes:
 * - On the field row, tailwind-merge dropped `bfrs-border-border-control` when
 *   the error branch appended `bfrs-border-danger` — the border colors are
 *   exclusive.
 * - On the helper span, `bfrs-leading-5` [pos 49092] beats the line-height from
 *   `bfrs-text-xs` [pos 48728] — net line-height 1.25rem. */

const RevealFieldRoot = styled("div", {
  label: "AgenticRevealField-root"
})({
  display: "grid",
  gap: ".375rem"
});

const RevealFieldLabel = styled("span", {
  label: "AgenticRevealField-label"
})({
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const RevealFieldField = styled("div", {
  label: "AgenticRevealField-field",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { error: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    height: "2.25rem",
    alignItems: "center",
    borderRadius: "var(--bfrs-radius-lg)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    paddingLeft: ".75rem",
    paddingRight: ".75rem"
  },
  ownerState.error && {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
]);

/* The old code passed `bfrs-w-full` into RevealAndCopy's className; `&&`
 * reproduces the consumer-class-beats-component-class outcome. */
const RevealFieldReveal = styled(RevealAndCopy, {
  label: "AgenticRevealField-reveal"
})({
  "&&": { width: "100%" }
});

const RevealFieldHelper = styled("span", {
  label: "AgenticRevealField-helper",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { error: boolean } }>(({ ownerState }) => [
  {
    fontSize: ".75rem",
    lineHeight: "1.25rem"
  },
  ownerState.error
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
      }
]);

export const RevealField = forwardRef<HTMLDivElement, RevealFieldProps>(({ className, fieldClassName, label, helperText, errorText, ...props }, ref) => (
  <RevealFieldRoot ref={ref} className={className}>
    {label && <RevealFieldLabel>{label}</RevealFieldLabel>}
    <RevealFieldField ownerState={{ error: Boolean(errorText) }} className={fieldClassName}>
      <RevealFieldReveal {...props} />
    </RevealFieldField>
    {(helperText || errorText) && <RevealFieldHelper ownerState={{ error: Boolean(errorText) }}>{errorText ?? helperText}</RevealFieldHelper>}
  </RevealFieldRoot>
));

RevealField.displayName = "RevealField";
